import React, { useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Box, Typography, Button } from "@mui/material";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { addNewMessage, updateSearchResults } from "../../slice";
import { CANDIDATES_API, PROFILES_API } from "../../api";
import { useStrictDroppable, useCurrentState, useDate } from "../../hooks";
import CandidateAccordion from "../CandidateAccordion";
import InfiniteScroll from "../InfiniteScroll";
import ConfirmModal from "../ConfirmModal";

function Candidates() {
  const PAGE_SIZE = process.env.REACT_APP_PAGE_SIZE;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const updateUserStatus = useCurrentState();
  const { simpleGregorianToPersian } = useDate();
  const [candidates, setCandidates] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const { mutate: updateCandidatesOrder } = useMutation((body) =>
    CANDIDATES_API.UpdateCandidatesOrder(body)
  );

  const { mutate: sendCandidatesForApproval } = useMutation(
    () => PROFILES_API.SendCandidatesForApproval(),
    {
      onSuccess: ({ data }) => {
        if (data.success) {
          window.sessionStorage.setItem("waiting-for-candidate", data.message);
          updateUserStatus();
          navigate("/wait-for-candidate");
        } else {
          dispatch(
            addNewMessage({
              id: "failed-on-send-candidate-for-approval",
              text: data.message,
              type: "error",
            })
          );
        }
      },
      onError: (error) => {
        if (error.response?.data) {
          dispatch(
            addNewMessage({
              text: error.response.data.message,
              id: "failed-on-send-candidate-for-approval",
              type: "error",
            })
          );
        }
      },
      onSettled: () => setOpenConfirmModal(false),
    }
  );

  const handleRemoveCandidate = (data) => {
    if (data.success) {
      dispatch(updateSearchResults({ number: data.number, isCandidate: false }));
      if (page === 0) {
        getMyCandidates();
      } else {
        setPage(0);
      }
      dispatch(
        addNewMessage({
          id: "success-on-remove-candidate",
          text: data.message,
          type: "success",
        })
      );
    } else {
      dispatch(
        addNewMessage({
          id: "failed-on-remove-candidate",
          text: data.message,
          type: "error",
        })
      );
    }
  };

  const handleOnDragEnd = (result) => {
    const temp = Array.from(candidates);
    if (result.source && result.destination) {
      const [reorderedItem] = temp.splice(result.source.index, 1);
      temp.splice(result.destination.index, 0, reorderedItem);
      temp.forEach((item, index) => (item.orderIndex = index + 1));
      updateCandidatesOrder(
        temp.map((item) => {
          return {
            number: item.number,
            orderIndex: item.orderIndex,
          };
        }),
        {
          onSuccess: ({ data }) => {
            if (data.success) {
              setCandidates(temp);
            } else {
              dispatch(
                addNewMessage({
                  id: "failed-on-update-candidate-order",
                  text: "مشکلی پیش  آمده مجدد تلاش کنید.",
                  type: "error",
                })
              );
              //todo: inja byd error handle beshe
            }
          },
          onError: () => {
            dispatch(
              addNewMessage({
                id: "failed-on-update-candidate-order",
                text: "مشکلی پیش  آمده مجدد تلاش کنید.",
                type: "error",
              })
            );
          },
        }
      );
    }
  };

  const { refetch: getMyCandidates, isLoading } = useQuery(
    ["getMyCandidates", page],
    () => CANDIDATES_API.GetMyCandidates(page * PAGE_SIZE, PAGE_SIZE),
    {
      onSuccess: ({ data }) => {
        let temp = [...(page === 0 ? [] : candidates), ...data.data.candidates];
        setHasMore(temp.length < data.data.totalCount);
        setCandidates(data.data.candidates);
      },
    }
  );

  const [enabled] = useStrictDroppable(isLoading);

  return (
    <InfiniteScroll
      hasMore={hasMore}
      loadMore={() => setPage((prev) => prev + 1)}
      scrollTarget="scrollable_div"
      isLoading={isLoading}
      dataLength={candidates.length}
    >
      <ConfirmModal
        message="آیا از ارسال لیست کاندیدهای خود به سمت مرکز اطمینان دارید؟"
        open={openConfirmModal}
        onClose={() => setOpenConfirmModal(false)}
        onSubmit={sendCandidatesForApproval}
      />
      <Box
        width="100%"
        borderTop="1px solid #DFDFDF"
        display="flex"
        justifyContent="center"
        flexDirection="column"
        pt={4}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          flexDirection={{ xs: "column-reverse", sm: "column-reverse", md: "row" }}
          alignItems="center"
          width="100%"
        >
          <Button variant="m1" onClick={() => setOpenConfirmModal(true)}>
            ارسال درخواست به مرکز
          </Button>
          <Typography variant="h2" mb={{ xs: 2, sm: 2, md: 2 }}>
            متقاضی گرامی اولویت اول تا سوم کاندیدهای خود را با درگ‌کردن موارد، انتخاب نمائید. شما
            می‌توانید حداکثر 3 گزینه را انتخاب و به مرکز ارسال نمائید.
            <br />
            <Typography fontWeight="bold" mt={1}>
              راهنما:
            </Typography>
            <Typography variant="h2">
              درگ کردن روی کامپیوتر: دکمه چپ موس را روی مورد انتخابی نگه داشته و بکشید.
              <br />
              درگ کردن روی گوشی همراه: انگشت خود را روی مورد انتخابی نگه داشته و بکشید.
            </Typography>
          </Typography>
        </Box>
        <Box display="flex" justifyContent="flex-end" alignItems="center" width="100%" py={2}>
          <Typography variant="h2" color="#AAAAAA" mx={8}>
            کد همسان
          </Typography>
        </Box>
        <DragDropContext onDragEnd={handleOnDragEnd}>
          {enabled && (
            <Droppable droppableId="candidates">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {candidates.map(
                    (
                      {
                        birthDate,
                        educationDegree,
                        number,
                        commitmentRateName,
                        residenceCityName,
                        id,
                      },
                      index
                    ) => (
                      <Draggable key={index} draggableId={`${index}`} index={index}>
                        {(provided) => (
                          <CandidateAccordion
                            id={id}
                            key={id}
                            provided={provided}
                            innerRef={provided.innerRef}
                            birthDate={simpleGregorianToPersian(
                              birthDate.split("T").shift()
                            ).replaceAll("-", "/")}
                            educationDegree={educationDegree}
                            number={number}
                            isCandidate={true}
                            commitmentRateName={commitmentRateName}
                            residenceCityName={residenceCityName}
                            onRemoveCandidate={handleRemoveCandidate}
                          />
                        )}
                      </Draggable>
                    )
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          )}
        </DragDropContext>
      </Box>
    </InfiniteScroll>
  );
}

export default Candidates;
